import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Popup from "../../Popup/Popup";
import AppTable from "../../AppTable/AppTable";
import BackButton from "../../AppBarComponents/BackButton";
import { clearPartMentions, getPartMentions } from "../../../redux/partSlice";
import { OutlineButton } from "../../Buttons";
import { getPartMentionsRevisionsForPart } from "../../../redux/procedureRevisionSlice";
import SelectRevisionModal from "../../../pages/AddModule/SelectRevisionModal";
import { getAllModules } from "../../../redux/moduleSlice";

const normalizeCellValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "-";
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return value;
};

const isHtmlString = (value) =>
  typeof value === "string" && /<\/?[a-z][\s\S]*>/i.test(value);

const formatStatus = (status) => {
  if (!status || typeof status !== "string") {
    return "-";
  }
  // Replace underscores with spaces and capitalize first letter of each word
  return status
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const PartMentionsPopup = ({ part, onClose, onCloseParentPopup = null }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    partMentions,
    partMentionsPagination,
    partMentionsLoading,
    partMentionsError,
    partMentionsIsModule,
    modulesMentions,
    modulesMentionsPagination,
    modulesMentionsLoading,
    modulesMentionsError,
    parts,
  } = useSelector((state) => state.parts);
  const { partMentionsRevisionData } = useSelector(
    (state) => state.procedureRevision
  );
  const { modules } = useSelector((state) => state.module);

  const [limit, setLimit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [searchType, setSearchType] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [revisionTableModalOpen, setRevisionTableModalOpen] = useState(false);
  const [currentPartInfo, setCurrentPartInfo] = useState({});
  const [modulesPopupOpen, setModulesPopupOpen] = useState(false);
  const [modulesLimit, setModulesLimit] = useState(20);
  const [modulesOffset, setModulesOffset] = useState(0);
  const [modulesSearch, setModulesSearch] = useState("");
  const [modulesSearchType, setModulesSearchType] = useState("");
  const [modulesSortBy, setModulesSortBy] = useState("");
  const [modulesSortOrder, setModulesSortOrder] = useState("");

  useEffect(() => {
    if (!part) {
      return;
    }

    // Call for procedures (show_procedure=true) - this updates Redux state
    dispatch(
      getPartMentions({
        partNumber: part.part_number,
        partId: part.part_id,
        limit,
        offset,
        search,
        searchType: searchType || undefined,
        sortBy: sortBy || undefined,
        sortOrder: sortOrder || undefined,
        showModule: false, // show_procedure=true
      })
    );
  }, [dispatch, part, limit, offset, search, searchType, sortBy, sortOrder]);

  // Separate useEffect for modules API call with search/filter/sort parameters
  useEffect(() => {
    if (!part) {
      return;
    }

    // Map column field names to API field names for search_type
    let mappedSearchType = modulesSearchType;
    if (modulesSearchType && modulesSearchType !== "all_columns") {
      mappedSearchType = modulesSearchType
        .split(",")
        .map((col) => {
          const trimmed = col.trim();
          // Map UI column field names to API field names
          if (trimmed === "referenced_in_procedures_table") {
            return "is_module_source";
          }
          if (trimmed === "entered_as_flowchart") {
            return "is_flowchart_source";
          }
          return trimmed;
        })
        .join(",");
    }

    // Call for modules (show_module=true) - stored in separate Redux state
    // This will be called when modules search/filter/sort/pagination changes
    dispatch(
      getPartMentions({
        partNumber: part.part_number,
        partId: part.part_id,
        limit: modulesLimit,
        offset: modulesOffset,
        search: modulesSearch || undefined,
        searchType: mappedSearchType || undefined,
        sortBy: modulesSortBy || undefined,
        sortOrder: modulesSortOrder || undefined,
        showModule: true, // show_module=true
      })
    );
  }, [
    dispatch,
    part,
    modulesLimit,
    modulesOffset,
    modulesSearch,
    modulesSearchType,
    modulesSortBy,
    modulesSortOrder,
  ]);

  useEffect(() => {
    // Reset pagination and filters when part changes
    setLimit(20);
    setOffset(0);
    setSearch("");
    setSearchType("");
    setSortBy("");
    setSortOrder("");
  }, [part?.part_id]);

  // Fetch revisions when part is available to get the count
  useEffect(() => {
    if (!part?.part_id) {
      return;
    }

    // Fetch revisions for the part to get the count
    dispatch(
      getPartMentionsRevisionsForPart({
        partId: part.part_id,
      })
    );
  }, [dispatch, part?.part_id]);

  // Fetch modules when component mounts to enable navigation
  useEffect(() => {
    if (!modules || modules.length === 0) {
      dispatch(getAllModules({ limit: 1000, offset: 0 }));
    }
  }, [dispatch, modules]);

  useEffect(() => {
    return () => {
      dispatch(clearPartMentions());
    };
  }, [dispatch]);

  const itemCode = part?.item_code || part?.part_number || "";

  const columns = useMemo(
    () => [
      {
        headName: "Type",
        field: "type",
        sort: true,
        filter: true,
      },
      {
        headName: "Item Code",
        field: "part_number",
        sort: true,
        filter: true,
      },
      {
        headName: "Rev",
        field: "rev",
        sort: true,
        filter: true,
      },
      {
        headName: "Status",
        field: "status_changed_to",
        sort: true,
        filter: true,
      },
      {
        headName: "Description",
        field: "description",
        sort: true,
        filter: true,
        width: "220px",
      },
      {
        headName: "Referenced In?",
        field: "referenced_in",
        sort: false,
        filter: true,
        width: "150px",
      },
      {
        headName: "Qty Usage Per Document in Table",
        field: "total_qty_part_required",
        sort: true,
        filter: true,
      },
      {
        headName: "Qty Usage Per Document In Text Boxes",
        field: "total_qty_text_box_content",
        sort: true,
        filter: true,
      },
    ],
    []
  );

  const handleNavigateToRevisionSubmission = useCallback(
    (row) => {
      if (!row) {
        return;
      }

      const partId = row.part_ref ?? part?.part_id ?? part?.partId ?? "";
      const revisionId =
        row.procedure_rev_ref ??
        row.procedure_revision_id ??
        row.proc_rev_id ??
        "";
      const revision = row.rev ?? "";
      const type = row.type ?? "type";
      const partNumber =
        row.part_number ?? part?.part_number ?? part?.partNumber ?? "";
      const descriptionRaw = row.description ?? "";

      if (!partId || !revision || !type || !partNumber || !revisionId) {
        console.warn(
          "Unable to navigate to revision submission. Missing data:",
          {
            partId,
            revision,
            type,
            partNumber,
            revisionId,
          }
        );
        return;
      }

      const safeDescription =
        descriptionRaw && descriptionRaw.trim().length > 0
          ? encodeURIComponent(descriptionRaw)
          : "no-description";

      const navigationPath = `/revision-submission/${encodeURIComponent(
        partId
      )}/${encodeURIComponent(revision)}/${encodeURIComponent(
        type
      )}/${encodeURIComponent(partNumber)}/${encodeURIComponent(
        revisionId
      )}/${safeDescription}`;

      // Close modules popup if open
      setModulesPopupOpen(false);

      // Close the popup before navigating
      if (typeof onClose === "function") {
        onClose();
      }

      // Close parent popup (PartsPopup) if provided
      if (typeof onCloseParentPopup === "function") {
        onCloseParentPopup();
      }

      // Wait for popup cleanup to complete before navigating
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Reset overflow styles after popup cleanup
          document.documentElement.style.overflow = "";
          document.body.style.overflow = "";
          navigate(navigationPath);
        });
      });
    },
    [navigate, part, onClose, onCloseParentPopup]
  );

  const handleBack = useCallback(() => {
    if (typeof onClose === "function") {
      onClose();
      return;
    }

    navigate(-1);
  }, [navigate, onClose]);

  const handleFilterSort = useCallback(
    (queryString) => {
      const params = new URLSearchParams(queryString);

      const limitParam = Number(params.get("limit"));
      if (!Number.isNaN(limitParam) && limitParam > 0 && limitParam !== limit) {
        setLimit(limitParam);
      }

      const offsetParam = Number(params.get("offset"));
      if (
        !Number.isNaN(offsetParam) &&
        offsetParam >= 0 &&
        offsetParam !== offset
      ) {
        setOffset(offsetParam);
      }

      const searchValue = params.get("search") || "";
      if (searchValue !== search) {
        setSearch(searchValue);
      }

      const searchTypeValue = params.get("search_type") || "";
      if (searchTypeValue !== searchType) {
        setSearchType(searchTypeValue);
      }

      let sortByValue = params.get("sort_by") || "";
      // Map "name" back to "type" since AppTable converts "type" to "name" but our API expects "type"
      if (sortByValue === "name") {
        sortByValue = "type";
      }
      if (sortByValue !== sortBy) {
        setSortBy(sortByValue);
      }

      const sortOrderValue = params.get("sort_order") || "";
      if (sortOrderValue !== sortOrder) {
        setSortOrder(sortOrderValue);
      }
    },
    [limit, offset, search, searchType, sortBy, sortOrder]
  );

  const totalItems =
    partMentionsPagination?.total ??
    (Array.isArray(partMentions) ? partMentions.length : 0);

  const effectiveLimit = limit || partMentionsPagination?.limit || 20;
  const totalPages =
    partMentionsPagination?.totalPages ??
    Math.max(1, Math.ceil(totalItems / (effectiveLimit || 1)));

  const columnsWithAction = [
    ...columns,
    {
      headName: "Action",
      field: "__action",
      textCenter: true,
      width: "60px",
    },
  ];

  const filteredPartMentions = useMemo(() => {
    if (!Array.isArray(partMentions) || partMentions.length === 0) {
      return [];
    }

    const normalizedItemCode = (itemCode || "").toString().trim().toLowerCase();

    const filtered = partMentions.filter((row) => {
      // Exclude rows where is_module_source === true OR is_flowchart_source === true
      if (row?.is_module_source === true || row?.is_flowchart_source === true) {
        return false;
      }

      // Filter: exclude rows where the row's part_number matches the current part's itemCode
      // This prevents showing the part mentioning itself
      // But we want to show all other parts that mention this part
      if (normalizedItemCode) {
        const rowItemCode = (row?.part_number || row?.item_code || "")
          .toString()
          .trim()
          .toLowerCase();

        // Only exclude if the row's part_number exactly matches the itemCode
        // All other rows should be included
        if (rowItemCode === normalizedItemCode) {
          return false;
        }
      }

      // Note: We show all rows regardless of total_qty_text_box_content or total_qty_part_required values
      // Rows with both quantities as 0 are still shown

      return true;
    });

    return filtered;
  }, [itemCode, partMentions]);

  const getRowKey = (row, index) =>
    row?.id ??
    `${row?.part_ref ?? "row"}_${row?.procedure_rev_ref ?? index}_${index}`;

  // Count modules from modulesMentions (from show_module=true API call)
  const moduleCount = useMemo(() => {
    if (!Array.isArray(modulesMentions)) {
      return 0;
    }
    // Count all rows from modulesMentions (they're all modules/flowcharts)
    return modulesMentions.length;
  }, [modulesMentions]);

  // Filter modules from partMentions (include items with is_module_source === true OR is_flowchart_source === true)
  // This is used for the count only
  const moduleRowsForCount = useMemo(() => {
    if (!Array.isArray(partMentions)) {
      return [];
    }
    return partMentions.filter((row) => {
      return (
        row?.is_module_source === true || row?.is_flowchart_source === true
      );
    });
  }, [partMentions]);

  // Use modulesMentions from Redux for the modules popup data
  // API handles filtering, sorting, and pagination, so use data directly
  const moduleRows = useMemo(() => {
    if (!Array.isArray(modulesMentions)) {
      return [];
    }
    return modulesMentions;
  }, [modulesMentions]);

  // Handler for modules filter/sort
  const handleModulesFilterSort = useCallback(
    (queryString) => {
      const params = new URLSearchParams(queryString);

      const limitParam = Number(params.get("limit"));
      if (
        !Number.isNaN(limitParam) &&
        limitParam > 0 &&
        limitParam !== modulesLimit
      ) {
        setModulesLimit(limitParam);
      }

      const offsetParam = Number(params.get("offset"));
      if (
        !Number.isNaN(offsetParam) &&
        offsetParam >= 0 &&
        offsetParam !== modulesOffset
      ) {
        setModulesOffset(offsetParam);
      }

      const searchValue = params.get("search") || "";
      if (searchValue !== modulesSearch) {
        setModulesSearch(searchValue);
      }

      const searchTypeValue = params.get("search_type") || "";
      if (searchTypeValue !== modulesSearchType) {
        setModulesSearchType(searchTypeValue);
      }

      let sortByValue = params.get("sort_by") || "";
      // Map "name" back to "type" since AppTable converts "type" to "name" but we need "type"
      if (sortByValue === "name") {
        sortByValue = "type";
      }
      if (sortByValue !== modulesSortBy) {
        setModulesSortBy(sortByValue);
      }

      const sortOrderValue = params.get("sort_order") || "";
      if (sortOrderValue !== modulesSortOrder) {
        setModulesSortOrder(sortOrderValue);
      }
    },
    [
      modulesLimit,
      modulesOffset,
      modulesSearch,
      modulesSearchType,
      modulesSortBy,
      modulesSortOrder,
    ]
  );

  // Count revisions from partMentionsRevisionData
  const revisionCount = useMemo(() => {
    if (!Array.isArray(partMentionsRevisionData)) {
      return 0;
    }
    return partMentionsRevisionData.length;
  }, [partMentionsRevisionData]);

  const partNumber = part?.part_number || part?.item_code || itemCode || "";

  const handleNavigateToModules = useCallback(() => {
    if (!part) {
      return;
    }

    // Open the modules popup
    setModulesPopupOpen(true);

    // Modules data is already fetched when the popup opens (in useEffect)
    // If we need to refresh, we can dispatch here, but it's already loaded
  }, [part]);

  const handleCloseModulesPopup = useCallback(() => {
    // Reset modules search and sort state when closing
    setModulesSearch("");
    setModulesSearchType("");
    setModulesSortBy("");
    setModulesSortOrder("");
    setModulesOffset(0);
    setModulesLimit(20);
    setModulesPopupOpen(false);
  }, []);

  const handleBackModules = useCallback(() => {
    handleCloseModulesPopup();
  }, [handleCloseModulesPopup]);

  const handleNavigateToModule = useCallback(
    async (row) => {
      let modulesToSearch = modules;

      // Fetch modules if not already loaded
      if (!modulesToSearch || modulesToSearch.length === 0) {
        try {
          const result = await dispatch(
            getAllModules({ limit: 1000, offset: 0 })
          ).unwrap();
          // The API response structure may vary, try both possibilities
          modulesToSearch = result?.results || result || [];
        } catch (error) {
          console.error("Error fetching modules:", error);
          return;
        }
      }

      // Find the module by part_number
      const module = modulesToSearch?.find(
        (m) => m.module_part?.part_number === row.part_number
      );

      if (module) {
        // Close the modules popup first
        setModulesPopupOpen(false);

        // Close the parent popup (PartMentionsPopup) if provided
        if (typeof onClose === "function") {
          onClose();
        }

        // Close parent popup (PartsPopup) if provided
        if (typeof onCloseParentPopup === "function") {
          onCloseParentPopup();
        }

        // Wait for popup cleanup to complete before navigating
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Reset overflow styles after popup cleanup
            document.documentElement.style.overflow = "";
            document.body.style.overflow = "";
            // Navigate to AddModule with the module data
            navigate("/new-module", {
              state: { moduleData: module, isUnlink: false },
            });
          });
        });
      } else {
        console.warn("Module not found for part_number:", row.part_number);
      }
    },
    [modules, dispatch, navigate, onClose, onCloseParentPopup]
  );

  const handleNavigateToRevisions = useCallback(async () => {
    if (!part) {
      return;
    }

    // Set current part info for the modal
    setCurrentPartInfo({
      part_id: part.part_id,
      part_number: part.part_number || part.item_code,
      type_name: part.type_details?.name || part.type_name,
      description: part.description || "",
    });

    try {
      // Fetch revisions for the part
      await dispatch(
        getPartMentionsRevisionsForPart({
          partId: part.part_id,
        })
      );
      // Open the revision selection modal
      setRevisionTableModalOpen(true);
    } catch (error) {
      console.error("Error fetching revisions:", error);
    }
  }, [dispatch, part]);

  const handleNavigateToModuleFromButton = useCallback(async () => {
    // Fetch modules if not already loaded
    let modulesToSearch = modules;

    if (!modulesToSearch || modulesToSearch.length === 0) {
      try {
        const result = await dispatch(
          getAllModules({ limit: 1000, offset: 0 })
        ).unwrap();
        modulesToSearch = result?.results || result || [];
      } catch (error) {
        console.error("Error fetching modules:", error);
        return;
      }
    }

    // Find the module by part_number
    const module = modulesToSearch?.find(
      (m) => m.module_part?.part_number === part?.part_number
    );

    if (module) {
      // Close modules popup if open
      setModulesPopupOpen(false);

      // Close the popup before navigating
      if (typeof onClose === "function") {
        onClose();
      }

      // Close parent popup (PartsPopup) if provided
      if (typeof onCloseParentPopup === "function") {
        onCloseParentPopup();
      }

      // Wait for popup cleanup to complete before navigating
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Reset overflow styles after popup cleanup
          document.documentElement.style.overflow = "";
          document.body.style.overflow = "";
          // Navigate to AddModule with the module data
          navigate("/new-module", {
            state: { moduleData: module, isUnlink: false },
          });
        });
      });
    } else {
      console.warn("Module not found for part_number:", part?.part_number);
    }
  }, [modules, dispatch, navigate, part, onClose, onCloseParentPopup]);

  const handleRevisionSelect = useCallback(
    ({ procedureRevisionId, revision, status, partId }) => {
      // Add fallback for description when undefined or empty
      const description =
        currentPartInfo.description && currentPartInfo.description.length > 0
          ? currentPartInfo.description
          : "No Description";

      const common1 = `${currentPartInfo.type_name || currentPartInfo.type}/${
        currentPartInfo.part_number || currentPartInfo.partNumber
      }/${procedureRevisionId}`;
      const common2 = `${currentPartInfo.type_name || currentPartInfo.type}/${
        currentPartInfo.part_number || currentPartInfo.partNumber
      }/${description}`;

      setRevisionTableModalOpen(false);

      // Close modules popup if open
      setModulesPopupOpen(false);

      // Close the popup before navigating
      if (typeof onClose === "function") {
        onClose();
      }

      // Close parent popup (PartsPopup) if provided
      if (typeof onCloseParentPopup === "function") {
        onCloseParentPopup();
      }

      // Wait for popup cleanup to complete before navigating
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Reset overflow styles after popup cleanup
          document.documentElement.style.overflow = "";
          document.body.style.overflow = "";

          switch (status) {
            case "Not Submitted":
              navigate(
                `/revision-submission/${partId}/${revision}/${common1}/${description}`,
                { state: { status: "Not Submitted" } }
              );
              break;
            case "Change Requested":
              navigate(
                `/revision-submission/${partId}/${revision}/${common1}/${description}`,
                { state: { status: "Change Requested" } }
              );
              break;
            case "Submitted For Approval":
              navigate(
                `/approve-revision/${partId}/${revision}/${common1}/${description}`
              );
              break;
            case "Approved":
              navigate(
                `/release-revision/${partId}/${revision}/${common1}/${description}`
              );
              break;
            case "Released":
              navigate(
                `/released-revision/${partId}/${revision}/${common2}/${procedureRevisionId}`
              );
              break;
            case "Rejected":
              navigate(
                `/rejected-revision/${partId}/${revision}/${common2}/Rejected/${procedureRevisionId}`
              );
              break;
            case "Archived":
              navigate(
                `/archived-revision/${partId}/${revision}/${common2}/Archived/${procedureRevisionId}`
              );
              break;
            default:
          }
        });
      });
    },
    [navigate, currentPartInfo, onClose, onCloseParentPopup]
  );

  const paginationPosition = useMemo(
    () => ({
      position: "center",
      leftContent: null,
      rightContent: (
        <OutlineButton
          variant="outline-blue"
          size="md"
          onClick={handleNavigateToModules}
          disabled={moduleCount === 0}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            marginLeft: "15px",
            height: "32px",
            padding: "4px 12px",
            opacity: moduleCount === 0 ? 0.5 : 1,
            cursor: moduleCount === 0 ? "not-allowed" : "pointer",
          }}
        >
          <span>
            Modules where {partNumber} is Used ({moduleCount})
          </span>
          <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: "8px" }} />
        </OutlineButton>
      ),
    }),
    [handleNavigateToModules, partNumber, moduleCount]
  );

  return (
    <Popup
      title={
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            position: "relative",
            paddingLeft: "40px",
          }}
        >
          <span
            style={{
              position: "absolute",
              left: 0,
              display: "flex",
              alignItems: "center",
            }}
          >
            <BackButton onBack={handleBack} />
          </span>
          <span>{`Revisions Where ${itemCode} is Mentioned`}</span>
        </span>
      }
      closeModal={onClose}
      shouldScroll={false}
    >
      <AppTable
        columnsData={columnsWithAction}
        showSearchInput={true}
        contentAfterSearchFilter={
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            {partMentionsIsModule ? (
              <OutlineButton
                variant="outline-blue"
                size="md"
                onClick={handleNavigateToModuleFromButton}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "40px",
                  marginLeft: "15px",
                  height: "32px",
                  padding: "4px 12px",
                }}
              >
                <span>Go to Module</span>
                <FontAwesomeIcon
                  icon={faArrowRight}
                  style={{ marginLeft: "8px" }}
                />
              </OutlineButton>
            ) : (
              <OutlineButton
                variant="outline-blue"
                size="md"
                onClick={handleNavigateToRevisions}
                disabled={revisionCount === 0}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "40px",
                  marginLeft: "15px",
                  height: "32px",
                  padding: "4px 12px",
                  opacity: revisionCount === 0 ? 0.5 : 1,
                  cursor: revisionCount === 0 ? "not-allowed" : "pointer",
                }}
              >
                <span>Go to Revision ({revisionCount})</span>
                <FontAwesomeIcon
                  icon={faArrowRight}
                  style={{ marginLeft: "8px" }}
                />
              </OutlineButton>
            )}
          </div>
        }
        paginate={true}
        pagination={{
          limit: [limit, setLimit],
          offset: [offset, setOffset],
          pagination: {
            total: totalItems,
            totalPages,
          },
          paginationPosition,
          paginationShadow: true,
        }}
        onFilterSortHandler={handleFilterSort}
        usingInPopup={true}
      >
        {partMentionsLoading ? (
          <tr>
            <td
              colSpan={columnsWithAction.length}
              style={{ textAlign: "center" }}
            >
              Loading...
            </td>
          </tr>
        ) : partMentionsError ? (
          <tr>
            <td
              colSpan={columnsWithAction.length}
              style={{ textAlign: "center" }}
            >
              {partMentionsError}
            </td>
          </tr>
        ) : filteredPartMentions && filteredPartMentions.length > 0 ? (
          filteredPartMentions.map((row, index) => {
            // Check if part is used in steps for this specific row
            // Use row-specific fields: part_required_source indicates usage in steps
            const hasSteps = row?.part_required_source === true;

            // Check if part is used in text box content (canvas) for this specific row
            // Use row-specific field: text_box_content_source indicates usage in canvas
            // Also check that total_qty_text_box_content is not 0
            const totalQtyTextBoxContent = row?.total_qty_text_box_content;
            const hasCanvas =
              row?.text_box_content_source === true &&
              totalQtyTextBoxContent !== 0 &&
              totalQtyTextBoxContent !== "0" &&
              totalQtyTextBoxContent !== null &&
              totalQtyTextBoxContent !== undefined;

            return (
              <tr key={getRowKey(row, index)}>
                {columns.map((column) => {
                  if (column.field === "referenced_in") {
                    return (
                      <td key={column.field}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                            padding: "8px 0",
                          }}
                        >
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              fontSize: "13px",
                              cursor: "default",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={hasSteps}
                              readOnly
                              className="blue-checkbox"
                              style={{
                                cursor: "default",
                                accentColor: "#0D6EFD",
                                pointerEvents: "none",
                                opacity: 0.5,
                              }}
                            />
                            <span>Step(s)</span>
                          </label>
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              fontSize: "13px",
                              cursor: "default",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={hasCanvas}
                              readOnly
                              className="blue-checkbox"
                              style={{
                                cursor: "default",
                                accentColor: "#0D6EFD",
                                pointerEvents: "none",
                                opacity: 0.5,
                              }}
                            />
                            <span>Canvas(es)</span>
                          </label>
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              fontSize: "13px",
                              cursor: "default",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={false}
                              readOnly
                              className="blue-checkbox"
                              style={{
                                cursor: "default",
                                accentColor: "#0D6EFD",
                                pointerEvents: "none",
                                opacity: 0.5,
                              }}
                            />
                            <span>Reference(es)</span>
                          </label>
                        </div>
                      </td>
                    );
                  }

                  const cellValue = row[column.field];

                  // Format status field specially
                  if (column.field === "status_changed_to") {
                    return (
                      <td key={column.field}>{formatStatus(cellValue)}</td>
                    );
                  }

                  return (
                    <td key={column.field}>
                      {isHtmlString(cellValue) ? (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: cellValue,
                          }}
                        />
                      ) : (
                        normalizeCellValue(cellValue)
                      )}
                    </td>
                  );
                })}
                <td style={{ textAlign: "center" }}>
                  <button
                    className="right-arrow"
                    onClick={() => {
                      handleNavigateToRevisionSubmission(row);
                    }}
                  >
                    <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                </td>
              </tr>
            );
          })
        ) : (
          <tr>
            <td
              colSpan={columnsWithAction.length}
              style={{ textAlign: "center" }}
            >
              No usage found for this part.
            </td>
          </tr>
        )}
      </AppTable>

      {/* Select Revision Modal */}
      <SelectRevisionModal
        isOpen={revisionTableModalOpen}
        onClose={() => setRevisionTableModalOpen(false)}
        revisionData={partMentionsRevisionData || []}
        currentPartInfo={currentPartInfo}
        onRevisionSelect={handleRevisionSelect}
      />

      {/* Modules Popup */}
      {modulesPopupOpen && (
        <Popup
          title={
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                position: "relative",
                paddingLeft: "40px",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  left: 0,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <BackButton onBack={handleBackModules} />
              </span>
              <span>{`Modules Where ${partNumber} is Mentioned`}</span>
            </span>
          }
          closeModal={handleCloseModulesPopup}
        >
          <AppTable
            columnsData={[
              {
                headName: "Type",
                field: "type",
                sort: true,
                filter: true,
              },
              {
                headName: "Item Code",
                field: "part_number",
                sort: true,
                filter: true,
              },
              {
                headName: "Description",
                field: "description",
                sort: true,
                filter: true,
                width: "220px",
              },
              {
                headName: "Referenced In Procedures Table?",
                field: "referenced_in_procedures_table",
                sort: false,
                filter: true,
                width: "200px",
              },
              {
                headName: "Entered as Flowchart?",
                field: "entered_as_flowchart",
                sort: false,
                filter: true,
                width: "180px",
              },
              {
                headName: "Action",
                field: "action",
                sort: false,
                filter: false,
                width: "60px",
              },
            ]}
            showSearchInput={true}
            paginate={true}
            pagination={{
              limit: [modulesLimit, setModulesLimit],
              offset: [modulesOffset, setModulesOffset],
              pagination: {
                total:
                  modulesMentionsPagination?.total ??
                  (Array.isArray(moduleRows) ? moduleRows.length : 0),
                totalPages:
                  modulesMentionsPagination?.totalPages ??
                  Math.max(
                    1,
                    Math.ceil(
                      (modulesMentionsPagination?.total ??
                        (Array.isArray(moduleRows) ? moduleRows.length : 0)) /
                        (modulesLimit || 1)
                    )
                  ),
              },
              paginationPosition: {
                position: "center",
                leftContent: null,
                rightContent: null,
              },
              paginationShadow: true,
            }}
            onFilterSortHandler={handleModulesFilterSort}
            usingInPopup={true}
          >
            {modulesMentionsLoading ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center" }}>
                  Loading...
                </td>
              </tr>
            ) : modulesMentionsError ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center" }}>
                  {modulesMentionsError}
                </td>
              </tr>
            ) : moduleRows && moduleRows.length > 0 ? (
              moduleRows.map((row, index) => {
                // Use API response fields directly
                // Check if part is referenced in procedures table (is_module_source)
                const referencedInProceduresTable =
                  row?.is_module_source === true;

                // Check if part is entered as flowchart
                const enteredAsFlowchart = row?.is_flowchart_source === true;

                return (
                  <tr key={getRowKey(row, index)}>
                    <td>{normalizeCellValue(row.type)}</td>
                    <td>{normalizeCellValue(row.part_number)}</td>
                    <td>
                      {isHtmlString(row.description) ? (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: row.description,
                          }}
                        />
                      ) : (
                        normalizeCellValue(row.description)
                      )}
                    </td>
                    <td>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          fontSize: "13px",
                          cursor: "default",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={referencedInProceduresTable}
                          readOnly
                          className="blue-checkbox"
                          style={{
                            cursor: "default",
                            accentColor: "#0D6EFD",
                            pointerEvents: "none",
                            opacity: 0.5,
                          }}
                        />
                        <span>Yes</span>
                      </label>
                    </td>
                    <td>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          fontSize: "13px",
                          cursor: "default",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={enteredAsFlowchart}
                          readOnly
                          className="blue-checkbox"
                          style={{
                            cursor: "default",
                            accentColor: "#0D6EFD",
                            pointerEvents: "none",
                            opacity: 0.5,
                          }}
                        />
                        <span>Yes</span>
                      </label>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button
                        className="right-arrow"
                        onClick={() => {
                          handleNavigateToModule(row);
                        }}
                      >
                        <FontAwesomeIcon icon={faArrowRight} />
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={6} style={{ textAlign: "center" }}>
                  No modules found for this part.
                </td>
              </tr>
            )}
          </AppTable>
        </Popup>
      )}
    </Popup>
  );
};

export default PartMentionsPopup;
